"use client";

import { useTranslations } from "next-intl";
import React from "react";

interface Testimonial {
  name: string;
  memberSince: string;
  quote: string;
}

interface PhotoCard {
  isPhoto: true;
  image: string;
  alt: string;
}

type Card = Testimonial | PhotoCard;

const TestimonialsSection = () => {
  const t = useTranslations("HomePageTestimonials");
  const testimonials: Testimonial[] = t.raw("testimonials");

  const cards: Card[] = [
    ...testimonials.slice(0, 3),
    {
      isPhoto: true,
      image: "/assets/v2/images/Flightshomepage4.png",
      alt: "Travel inspiration",
    },
    ...testimonials.slice(3),
  ];

  const duplicatedCards: Card[] = [...cards, ...cards];

  const slideOffset = cards.length * 320;

  return (
    <section className="w-full py-8 bg-white dark:bg-dark text-sleap-gray-950 dark:text-white rounded-xl dark:rounded-none m-0 md:py-16 bg-gradient-to-t from-cyan-100 to-white/0 dark:bg-gradient-to-b dark:from-teal-950/0 dark:to-teal-950">
      <div className="flex flex-col items-center gap-10 pb-16">
        <div className="text-center text-5xl font-semibold font-inter">
          <span className="bg-gradient-to-b from-sleap-blue-100 to-sleap-purple-300 bg-clip-text text-transparent">
            {t("lovedBy")}
          </span>{" "}
          <span className="text-sleap-gray-950 dark:text-white">
            {t("manyTravellers")}
          </span>
        </div>

        <div className="w-full overflow-hidden relative">
          <div
            className="flex gap-5 flex-nowrap slide-left"
            style={{
              width: `${duplicatedCards.length * 320}px`,
            }}
          >
            {duplicatedCards.map((card, index) => (
              <div
                key={`${"name" in card ? card.name : "photo"}-${index}`}
                className="w-[300px] min-h-[458px] flex-shrink-0 relative rounded-[20px] flex flex-col transition-opacity duration-300"
              >
                {"isPhoto" in card && card.isPhoto ? (
                  <div className="w-[300px] h-[458px] rounded-[20px] overflow-hidden">
                    <img
                      className="w-full h-full object-cover"
                      src={card.image}
                      alt={card.alt}
                    />
                  </div>
                ) : (
                  <div
                    className={`w-[300px] min-h-[458px] rounded-[20px] p-10 flex flex-col justify-between gap-6 ${
                      index % 2 === 0
                        ? "bg-sleap-purple-100"
                        : "bg-white shadow-rooms-card"
                    }`}
                  >
                    <div className="text-center text-xl font-normal font-inter text-sleap-gray-950 dark:text-sleap-gray-950">
                      {"quote" in card && card.quote}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-xl font-semibold font-inter text-sleap-gray-950 dark:text-sleap-gray-950">
                        {"name" in card && card.name}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes slideLeft {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${slideOffset}px);
          }
        }

        .slide-left {
          animation: slideLeft 60s linear infinite;
          animation-play-state: paused;
        }

        @media (min-width: 768px) {
          .slide-left:hover {
            animation-play-state: running;
          }
        }

        @media (max-width: 767px) {
          .slide-left {
            animation-play-state: running;
          }
        }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;
